export type CompareFn<T> = (a: T, b: T) => number
export type AccessorFn<T, U> = (item: T) => U

// for perf reasons it is better to reuse a collator instance, rather than call localeCompare repeatedly
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

/**
 * Returns a sorting function that sorts by a given accessor,
 * allowing by shallow or deep properties.
 * @example sortBy((item) => item.category.name)
 */
export function sortBy<T, U>(
  accessor: AccessorFn<T, U>,
  desc = false,
): CompareFn<T> {
  return (a: T, b: T) => {
    return desc
      ? collator.compare(
          accessor(b)?.toString() || '',
          accessor(a)?.toString() || '',
        )
      : collator.compare(
          accessor(a)?.toString() || '',
          accessor(b)?.toString() || '',
        )
  }
}

/**
 * Combines multiple compare functions into one, allowing for sorting by multiple properties.
 * The sort functions are applied in sequence, and the first non-zero result is returned.
 * @example
 * sortMulti([sortBy((user) => user.name), sortBy((user) => user.age)])
 */
export function sortMulti<T>(sortFns: CompareFn<T>[]): CompareFn<T> {
  // acts like OR-ing the sort functions in sequence
  return (a, b) => {
    for (const sort of sortFns) {
      const result = sort(a, b)

      if (result) {
        return result
      }
    }

    return 0
  }
}

export function getRange(start: number, end: number) {
  const length = end - start + 1

  return Array.from({ length }, (_, i) => start + i)
}

export function normalizeToArray<T>(value: T | T[] | null | undefined) {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
  }

  return [value]
}

/**
 * Returns a map of resources, keyed by the return value of the keyFn.
 * @example
 * const map = keyBy([{ id: 1, name: 'foo' }, { id: 2, name: 'bar' }], (item) => item.id)
 * // returns
 * {
 *   1: { id: 1, name: 'foo' },
 *   2: { id: 2, name: 'bar' }
 * }
 */
export function keyBy<T, U extends string>(
  array: readonly T[],
  keyFn: (item: T) => U,
): Record<U, T> {
  const map = {} as Record<U, T>

  for (const item of array) {
    map[keyFn(item)] = item
  }

  return map
}

/**
 * Groups the elements of a given iterable according to the string values returned by a provided callback function.
 * The returned object has separate properties for each group, containing arrays with the elements in the group.
 *
 * @example
 * const map = groupBy([
 *  { name: "bananas", type: "fruit", quantity: 0 },
 *  { name: "asparagus", type: "vegetables", quantity: 5 },
 *  { name: "cherries", type: "fruit", quantity: 5 }
 * ], (item) => item.type)
 * // returns
 * {
 *   fruit: [{ name: "bananas", type: "fruit", quantity: 0 }, { name: "cherries", type: "fruit", quantity: 5 }],
 *   vegetables: [{ name: "asparagus", type: "vegetables", quantity: 5 }]
 * }
 */
export function groupBy<T, U extends string>(
  array: readonly T[],
  keyFn: (item: T) => U,
) {
  const map = {} as Record<U, T[]>

  for (const item of array) {
    const key = keyFn(item)
    map[key] ??= []
    map[key].push(item)
  }

  return map
}

/**
 * Type guard function that enables union type narrowing with array inclusion checks.
 *
 * When using Array.includes() with union types, TypeScript cannot narrow the type
 * because includes() is not a type guard. The return type of includes() is just boolean,
 * which doesn't carry type information.
 *
 * This function uses a type predicate (`value is U`) to tell TypeScript that when
 * the function returns true, the value must be of type U (the union subset we're checking against).
 *
 * @example
 * type Status = 'draft' | 'published' | 'archived'
 * const status: Status = 'draft'
 *
 * // With Array.includes:
 * if (['draft', 'published'].includes(status)) {
 *   status // still typed as 'draft' | 'published' | 'archived'
 * }
 *
 * // With isOneOf:
 * if (isOneOf(status, ['draft', 'published'] as const)) {
 *   status // narrowed to 'draft' | 'published'
 * }
 */
export function isOneOf<T extends string, U extends T>(
  value: T,
  allowedValues: readonly U[],
): value is U {
  return allowedValues.includes(value as U)
}
